import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flexbox } from '@/components/flex';
import { breakpoints } from '@/components/responsive';

export interface TabSelectProps {
  defaultValue?: string;
  tabs: string[];
  disabledTabs?: string[];
  tabWidth?: number;
  tabHeight?: number;
  tabRadius?: number;
  tabPadding?: string;
  tabFont?: string;
  tabFontSize?: string;
  containerPadding?: number;
  onTabSelected?: (idx: number, name: string) => void;
  selectedColor?: string;
  unselectedColor?: string;
  disabledColor?: string;
  className?: string;
  fluid?: boolean;
  maxTabWidth?: number;
}

export function TabSelect({
  tabs,
  disabledTabs = [],
  tabWidth = 10,
  tabHeight = 5,
  tabRadius = 10,
  tabPadding = `0`,
  tabFont = `JetBrains Mono, serif`,
  tabFontSize,
  containerPadding = 0.5,
  onTabSelected,
  defaultValue,
  selectedColor,
  unselectedColor,
  disabledColor,
  className,
  fluid = false,
  maxTabWidth,
}: TabSelectProps) {
  const [selectedTab, setSelectedTab] = useState({
    idx: Math.max(
      tabs.findIndex((tab) => tab === defaultValue),
      0,
    ),
    name: defaultValue || tabs[0],
  });
  const selectorRef = useRef(null);

  const calcSelectorLeft = (idx: number) => {
    if (!fluid) return `${containerPadding + idx * tabWidth}rem`;
    const slot = calcSlotWidth(containerPadding, tabs.length, maxTabWidth);
    return `calc(${containerPadding}rem + ${idx} * ${slot})`;
  };

  const onTabSelect = (item: string) => () => {
    const idx = tabs.indexOf(item);
    if (selectorRef.current) {
      selectorRef.current.style.left = calcSelectorLeft(idx);
    }
    setSelectedTab({ idx, name: item });
    if (onTabSelected) onTabSelected(idx, item);
  };

  useEffect(() => {
    if (!selectorRef.current) return;
    const rawIdx = tabs.indexOf(defaultValue);
    const idx = rawIdx >= 0 ? rawIdx : 0;
    selectorRef.current.style.left = calcSelectorLeft(idx);
    setSelectedTab({ idx, name: tabs[idx] });
  }, [defaultValue, tabs, fluid, maxTabWidth, containerPadding, tabWidth]);

  return (
    <TabContainer
      className={`tab-select-container ${className}`}
      direction="row"
      radius={tabRadius}
      padding={containerPadding}
      $fluid={fluid}
      $numTabs={tabs.length}
      $maxTabWidth={maxTabWidth}
    >
      {tabs.map((item) => (
        <Tab
          key={item}
          className={`tab-select-tab tab-${item}`}
          height={tabHeight}
          width={tabWidth}
          selected={selectedTab.name === item}
          onClick={onTabSelect(item)}
          padding={tabPadding}
          disabled={disabledTabs.includes(item)}
          font={tabFont}
          tabFontSize={tabFontSize}
          selectedColor={selectedColor}
          unselectedColor={unselectedColor}
          disabledColor={disabledColor}
          $fluid={fluid}
          $maxTabWidth={maxTabWidth}
        >
          {item}
        </Tab>
      ))}
      <Selector
        className="tab-select-selector"
        ref={selectorRef}
        height={tabHeight}
        width={tabWidth}
        radius={tabRadius}
        $fluid={fluid}
        $numTabs={tabs.length}
        $containerPadding={containerPadding}
        $maxTabWidth={maxTabWidth}
      />
    </TabContainer>
  );
}

interface FluidProps {
  $fluid?: boolean;
  $maxTabWidth?: number;
}

interface TabContainerProps extends FluidProps {
  radius: number;
  padding: number;
  $numTabs?: number;
}

interface TabProps extends FluidProps {
  width: number;
  height: number;
  selected?: boolean;
  font?: string;
  tabFontSize?: string;
  selectedColor?: string;
  unselectedColor?: string;
  disabledColor?: string;
  padding?: string;
  disabled?: boolean;
}

interface SelectorProps extends FluidProps {
  width: number;
  height: number;
  radius: number;
  $numTabs?: number;
  $containerPadding?: number;
}

const calcSlotWidth = (
  padding: number,
  numTabs: number,
  maxTabWidth?: number,
) => {
  const slot = `(100% - ${2 * padding}rem) / ${numTabs}`;
  return maxTabWidth ? `min(${slot}, ${maxTabWidth}rem)` : slot;
};

const calcContainerWidth = (p: TabContainerProps) => {
  if (!p.$fluid) return `max-content`;
  if (p.$maxTabWidth && p.$numTabs)
    return `min(100%, ${p.$maxTabWidth * p.$numTabs + 2 * p.padding}rem)`;
  return `100%`;
};

const calcSelectorWidth = (p: SelectorProps) => {
  if (!p.$fluid) return `${p.width}rem`;
  const slot = calcSlotWidth(p.$containerPadding, p.$numTabs, p.$maxTabWidth);
  return p.$maxTabWidth ? slot : `calc(${slot})`;
};

const getTabColor = (p: TabProps) => {
  if (!p.selected && p.disabledColor) return p.disabledColor;
  if (p.selected) return p.selectedColor || `black`;
  return p.unselectedColor || `#A6AFB8`;
};

const TabContainer = styled(Flexbox)<TabContainerProps>`
  && {
    width: ${(p) => calcContainerWidth(p)};
    position: relative;
    padding: ${(p) => p.padding}rem;
    flex-direction: row;
    background: #ebedef;
    border-radius: ${(p) => p.radius}rem;
    z-index: 20;
  }
`;

const Tab = styled(Flexbox)<TabProps>`
  && {
    width: ${(p) => (p.$fluid ? `auto` : `${p.width}rem`)};
    flex: ${(p) => (p.$fluid ? `1` : `none`)};
    max-width: ${(p) =>
      p.$fluid && p.$maxTabWidth ? `${p.$maxTabWidth}rem` : `none`};
    height: ${(p) => p.height}rem;
    align-items: center;
    justify-content: center;
    font-family: ${(p) => p.font};
    font-size: ${(p) => p.tabFontSize || `1.1rem`};
    font-weight: ${(p) => (p.selected ? `900` : `500`)};
    color: ${(p) => getTabColor(p)};
    user-select: none;
    cursor: pointer;
    z-index: 25;
    transition: color, 0.3s linear;
    text-align: center;
    padding: ${(p) => p.padding};
    pointer-events: ${(p) => (p.disabled ? `none` : `auto`)};
    text-decoration: ${(p) => (p.disabled ? `line-through` : `none`)};
    opacity: ${(p) => (p.disabled && !p.disabledColor ? `0.5` : `1`)};
  }
  ${breakpoints.up(`md`)} {
    :hover {
      color: black;
    }
  }
`;

const Selector = styled.div<SelectorProps>`
  && {
    display: block;
    position: absolute;
    left: ${(p) => `${p.$containerPadding ?? 0.5}rem`};
    width: ${(p) => calcSelectorWidth(p)};
    height: ${(p) => p.height}rem;
    background-color: white;
    border: 0.13rem solid rgba(211, 216, 229, 0.37);
    box-shadow: 0 4px 9px 0 rgba(0, 0, 0, 0.16);
    border-radius: ${(p) => p.radius}rem;
    z-index: 22;
    transition: left ease-in-out 0.5s;
  }
`;
